import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { enrichCnpj } from "@/lib/cnpj.functions";
import { FORNECEDOR_ORIGEM, FORNECEDOR_STATUS } from "@/constants/enums";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle, 
  XCircle, 
  Edit, 
  Trash2, 
  Star, 
  ShieldCheck, 
  Search, 
  Filter,
  MoreVertical,
  ChevronDown,
  Plus,
  AlertCircle
} from "lucide-react";
import { FornecedorForm } from "@/components/admin/fornecedor-form";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";
import { registrarLogAdmin } from "@/utils/admin-logs";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/fornecedores")({
  component: FornecedoresAdmin,
});

const ESTADOS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];

function FornecedoresAdmin() {
  const { profile: adminProfile } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [currentTab, setCurrentTab] = useState<string>(FORNECEDOR_STATUS.PENDENTE);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filterSegmento, setFilterSegmento] = useState("all");
  const [filterUF, setFilterUF] = useState("all");
  const [filterOrigem, setFilterOrigem] = useState("all");

  const [rejectId, setRejectId] = useState<string | null>(null);
  const [editingFornecedor, setEditingFornecedor] = useState<any | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const { data: segmentos } = useQuery({
    queryKey: ["segmentos"],
    queryFn: async () => {
      const { data } = await supabase.from("segmentos").select("*").order("nome");
      return data || [];
    },
  });

  const { data: fornecedores, isLoading } = useQuery({
    queryKey: ["admin-fornecedores", currentTab, search, filterSegmento, filterUF, filterOrigem],
    queryFn: async () => {
      let query = supabase
        .from("fornecedores")
        .select(`
          *,
          segmentos(nome),
          denuncias(count)
        `)
        .eq('denuncias.status', 'aberta')
        .order("created_at", { ascending: false });

      if (currentTab !== "todos" && currentTab !== "with_reports") {
        if (currentTab === "no_cnpj") {
          query = query.or('cnpj.is.null,cnpj.eq.""');
        } else {
          query = query.eq("status", currentTab);
        }
      }

      if (currentTab === "with_reports") {
        // filter for those that have at least one report
        // This is tricky with current Supabase JS, so we fetch and filter locally or use a trick
        // query = query.not('denuncias', 'is', null); // This often doesn't work for count relations
      }

      if (search) {
        query = query.ilike("nome", `%${search}%`);
      }

      if (filterSegmento !== "all") {
        query = query.eq("segmento_id", filterSegmento);
      }

      if (filterUF !== "all") {
        query = query.eq("uf", filterUF);
      }

      if (filterOrigem !== "all") {
        query = query.eq("origem", filterOrigem);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      let filteredData = data;
      if (currentTab === "with_reports") {
        filteredData = data.filter(f => (f.denuncias as any)?.[0]?.count > 0);
      }
      
      return filteredData;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ ids, data }: { ids: string[]; data: any }) => {
      const { data: before } = await supabase.from("fornecedores").select("*").in("id", ids);
      
      const { data: updatedData, error } = await supabase
        .from("fornecedores")
        .update(data)
        .in("id", ids)
        .select();

      if (error) {
        console.error("Erro ao atualizar fornecedores:", error);
        throw error;
      }

      if (adminProfile) {
        for (const id of ids) {
          const fBefore = before?.find(b => b.id === id);
          const fAfter = updatedData?.find(a => a.id === id);
          await registrarLogAdmin({
            adminId: adminProfile.id,
            acao: "Atualização de fornecedor",
            tabela: "fornecedores",
            registroId: id,
            dadosAntes: fBefore,
            dadosDepois: fAfter
          });
        }
      }

      return { count: updatedData?.length || 0 };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-fornecedores"] });
      setSelectedIds([]);
      setRejectId(null);
      setRejectReason("");
      
      if (data.count === 0) {
        toast.warning("Nenhum registro foi alterado. Verifique as permissões.");
      } else {
        toast.success(`${data.count} fornecedor(es) atualizado(s)!`);
      }
    },
    onError: (error: any) => {
      toast.error(`Erro: ${error.message || "Falha ao atualizar fornecedores"}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { data: before } = await supabase.from("fornecedores").select("*").in("id", ids);

      const { data, error } = await supabase
        .from("fornecedores")
        .delete()
        .in("id", ids)
        .select();

      if (error) {
        console.error("Erro ao excluir fornecedores:", error);
        throw error;
      }

      if (adminProfile) {
        for (const id of ids) {
          const fBefore = before?.find(b => b.id === id);
          await registrarLogAdmin({
            adminId: adminProfile.id,
            acao: "Exclusão de fornecedor",
            tabela: "fornecedores",
            registroId: id,
            dadosAntes: fBefore,
            dadosDepois: null
          });
        }
      }

      return { count: data?.length || 0 };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-fornecedores"] });
      setSelectedIds([]);
      if (data.count === 0) {
        toast.warning("Nenhum registro foi excluído. Verifique as permissões.");
      } else {
        toast.success(`${data.count} fornecedor(es) excluído(s)!`);
      }
    },
    onError: (error: any) => {
      toast.error(`Erro: ${error.message || "Falha ao excluir fornecedores"}`);
    }
  });

  const handleToggle = (id: string, field: string, value: boolean) => {
    updateMutation.mutate({ ids: [id], data: { [field]: value } });
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pendente: "bg-amber-100 text-amber-700",
      aprovado: "bg-green-100 text-green-700",
      rejeitado: "bg-red-100 text-red-700",
    };
    return <Badge className={cn("capitalize shadow-none border-none", colors[status])}>{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-1 flex-col md:flex-row gap-4 items-center">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input 
              placeholder="Buscar por nome..." 
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            <Select value={filterSegmento} onValueChange={setFilterSegmento}>
              <SelectTrigger className="w-full md:w-[160px] h-10">
                <SelectValue placeholder="Segmento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Segmentos</SelectItem>
                {segmentos?.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterUF} onValueChange={setFilterUF}>
              <SelectTrigger className="w-[100px] h-10">
                <SelectValue placeholder="UF" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">UFs</SelectItem>
                {ESTADOS.map(uf => (
                  <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterOrigem} onValueChange={setFilterOrigem}>
              <SelectTrigger className="w-[140px] h-10">
                <SelectValue placeholder="Origem" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Origens</SelectItem>
                <SelectItem value={FORNECEDOR_ORIGEM.AUTOCADASTRO}>Autocadastro</SelectItem>
                <SelectItem value={FORNECEDOR_ORIGEM.IMPORTADO}>Importação</SelectItem>
                <SelectItem value={FORNECEDOR_ORIGEM.ADMIN}>Admin</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2 ml-2">
              <span className="text-sm font-medium">Com denúncias:</span>
              <Switch 
                checked={currentTab === 'with_reports'} 
                onCheckedChange={(checked) => setCurrentTab(checked ? 'with_reports' : FORNECEDOR_STATUS.PENDENTE)}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-10">
                  Ações em Lote ({selectedIds.length})
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => updateMutation.mutate({ ids: selectedIds, data: { status: FORNECEDOR_STATUS.APROVADO } })}>
                  <CheckCircle className="w-4 h-4 mr-2" /> Aprovar Selecionados
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => {
                  const toastId = toast.loading("Enriquecendo dados pelo CNPJ (1 req/s)...");
                  let count = 0;
                  let failed = 0;
                  
                  // Copiando para evitar problemas de concorrência com o estado
                  const idsToProcess = [...selectedIds];
                  
                  for (const id of idsToProcess) {
                    const f = fornecedores?.find(item => item.id === id);
                    if (f && f.cnpj) {
                      const cnpj = f.cnpj.replace(/\D/g, "");
                      if (cnpj.length === 14) {
                        try {
                          const result = await enrichCnpj({ data: { cnpj } });
                          if (result.success && result.data) {
                            const info = result.data;
                            const updateData: any = {};
                            
                            // Apenas preenche campos vazios
                            if (!f.razao_social || f.razao_social === f.nome) updateData.razao_social = info.razao_social;
                            if (!f.cidade) updateData.cidade = info.municipio;
                            if (!f.uf) updateData.uf = info.uf;
                            if (!f.email) updateData.email = info.email;
                            if (!f.telefone && info.telefone) updateData.telefone = info.telefone;
                            if (!f.descricao && info.cnae_fiscal_descricao) updateData.descricao = info.cnae_fiscal_descricao;
                            
                            // Se inativo, volta para pendente
                            if (info.descricao_situacao_cadastral && info.descricao_situacao_cadastral !== "ATIVA") {
                              updateData.status = FORNECEDOR_STATUS.PENDENTE;
                              updateData.motivo_rejeicao = `CNPJ ${info.descricao_situacao_cadastral} na Receita Federal`;
                            }

                            if (Object.keys(updateData).length > 0) {
                              await supabase.from("fornecedores").update(updateData).eq("id", id);
                              count++;
                            }
                          } else {
                            failed++;
                          }
                          // Delay de 1 segundo para respeitar rate limit
                          await new Promise(r => setTimeout(r, 1000));
                        } catch (e) {
                          failed++;
                          console.error("Batch enrichment failed for ID " + id, e);
                        }
                      }
                    }
                  }
                  toast.dismiss(toastId);
                  toast.success(`${count} registros enriquecidos! ${failed > 0 ? failed + ' falharam.' : ''}`);
                  queryClient.invalidateQueries({ queryKey: ["admin-fornecedores"] });
                  setSelectedIds([]);
                }}>
                  <ShieldCheck className="w-4 h-4 mr-2" /> Enriquecer dados pelo CNPJ
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateMutation.mutate({ ids: selectedIds, data: { destaque: true } })}>
                  <Star className="w-4 h-4 mr-2 text-amber-500 fill-amber-500" /> Destacar Selecionados
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => deleteMutation.mutate(selectedIds)} className="text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" /> Excluir Selecionados
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button 
            className="hidden md:flex bg-[#C6F764] text-[#0C1A12] hover:bg-[#b5e653] font-semibold h-10"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo fornecedor
          </Button>
        </div>
      </div>

      {/* FAB Mobile */}
      <Button 
        className="md:hidden fixed bottom-24 right-6 w-14 h-14 rounded-full bg-[#C6F764] text-[#0C1A12] shadow-lg z-50 p-0 hover:bg-[#b5e653]"
        onClick={() => setIsCreateModalOpen(true)}
        aria-label="Novo fornecedor"
      >
        <Plus className="w-6 h-6" />
      </Button>

      <Tabs value={currentTab} onValueChange={setCurrentTab}>
        <TabsList className="bg-white border">
          <TabsTrigger value={FORNECEDOR_STATUS.PENDENTE}>Pendentes</TabsTrigger>
          <TabsTrigger value={FORNECEDOR_STATUS.APROVADO}>Aprovados</TabsTrigger>
          <TabsTrigger value={FORNECEDOR_STATUS.REJEITADO}>Rejeitados</TabsTrigger>
          <TabsTrigger value="todos">Todos</TabsTrigger>
          <TabsTrigger value="no_cnpj">Sem CNPJ</TabsTrigger>
        </TabsList>

        <div className="bg-white border rounded-xl overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox 
                      checked={selectedIds.length > 0 && selectedIds.length === fornecedores?.length}
                      onCheckedChange={() => setSelectedIds(selectedIds.length === fornecedores?.length ? [] : (fornecedores?.map(f => f.id) || []))}
                    />
                  </TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Denúncias</TableHead>
                  <TableHead>Segmento</TableHead>
                  <TableHead>Local</TableHead>
                  <TableHead>Verificado</TableHead>
                  <TableHead>Destaque</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10">Carregando...</TableCell>
                  </TableRow>
                ) : (
                  fornecedores?.map((f) => (
                    <TableRow key={f.id}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedIds.includes(f.id)}
                          onCheckedChange={() => setSelectedIds(prev => prev.includes(f.id) ? prev.filter(i => i !== f.id) : [...prev, f.id])}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium flex items-center gap-2">
                          {f.nome}
                          {(!f.cnpj || f.cnpj.length < 14) && (
                            <AlertCircle className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{f.cnpj || "-"}</div>
                      </TableCell>
                      <TableCell>
                        {f.denuncias && (f.denuncias as any)[0]?.count > 0 ? (
                          <Badge variant="destructive" className="gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {(f.denuncias as any)[0].count}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {(f as any).segmentos?.nome || "-"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {f.cidade}/{f.uf}
                      </TableCell>
                      <TableCell>
                        <Switch 
                          checked={f.verificado || false} 
                          disabled={!f.cnpj}
                          onCheckedChange={(checked) => handleToggle(f.id, "verificado", checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch 
                          checked={f.destaque || false} 
                          onCheckedChange={(checked) => handleToggle(f.id, "destaque", checked)}
                        />
                      </TableCell>
                      <TableCell>{statusBadge(f.status)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                             <DropdownMenuItem onClick={() => setEditingFornecedor(f)}>
                               <Edit className="w-4 h-4 mr-2" /> Editar
                             </DropdownMenuItem>
                             <DropdownMenuItem onClick={() => updateMutation.mutate({ ids: [f.id], data: { status: FORNECEDOR_STATUS.APROVADO } })}>
                               <CheckCircle className="w-4 h-4 mr-2" /> Aprovar
                             </DropdownMenuItem>
                             <DropdownMenuItem onClick={() => setRejectId(f.id)} className="text-red-600">
                               <XCircle className="w-4 h-4 mr-2" /> Rejeitar
                             </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => deleteMutation.mutate([f.id])} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" /> Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </Tabs>

      <Dialog open={!!rejectId} onOpenChange={() => setRejectId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Fornecedor</DialogTitle>
            <DialogDescription>
              Informe o motivo da rejeição. O usuário verá esta mensagem.
            </DialogDescription>
          </DialogHeader>
          <Textarea 
            placeholder="Ex: CNPJ inválido, fotos de má qualidade..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectId(null)}>Cancelar</Button>
            <Button 
              variant="destructive" 
              onClick={() => updateMutation.mutate({ 
                ids: [rejectId!], 
                data: { status: FORNECEDOR_STATUS.REJEITADO, motivo_rejeicao: rejectReason } 
              })}
              disabled={!rejectReason}
            >
              Confirmar Rejeição
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Cadastro/Edição */}
      <Dialog 
        open={isCreateModalOpen || !!editingFornecedor} 
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateModalOpen(false);
            setEditingFornecedor(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFornecedor ? "Editar fornecedor" : "Cadastrar fornecedor"}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <FornecedorForm 
              initialData={editingFornecedor}
              onCancel={() => {
                setIsCreateModalOpen(false);
                setEditingFornecedor(null);
              }}
              onSuccess={(keepOpen) => {
                queryClient.invalidateQueries({ queryKey: ["admin-fornecedores"] });
                if (!keepOpen) {
                  setIsCreateModalOpen(false);
                  setEditingFornecedor(null);
                }
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

